import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useRef, useEffect, FC } from 'react';

interface MarkdownParagraphProps {
  content: string;
  enableLineAnimation?: boolean;
  animationDelay?: number;
  className?: string;
}

const MarkdownParagraph: FC<MarkdownParagraphProps> = ({ 
  content, 
  enableLineAnimation = false, 
  animationDelay = 0,
  className = '' 
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (enableLineAnimation && containerRef.current) {
      const elements = containerRef.current.querySelectorAll('.animated-element');
      elements.forEach((el, index) => {
        const element = el as HTMLElement;
        element.style.animationDelay = `${animationDelay + index * 0.03}s`;
      });
    }
  }, [enableLineAnimation, animationDelay, content]);

  const getAnimatedClassName = () => {
    if (!enableLineAnimation) return '';
    return 'animated-element fade-in-line';
  };

  return (
    <div ref={containerRef} className={`markdown-content ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: (props: any) => (
            <h1 className={`result-text text-3xl font-bold mb-6 mt-8 first:mt-0 border-b border-gray-700 pb-3 ${getAnimatedClassName()}`} {...props}>
              {props.children}
            </h1>
          ),
          h2: (props: any) => (
            <h2 className={`result-text text-2xl font-semibold mt-5 first:mt-0 ${getAnimatedClassName()}`} {...props}>
              {props.children}
            </h2>
          ),
          h3: (props: any) => (
            <h3 className={`result-text text-xl font-semibold mb-3 mt-6 first:mt-0 ${getAnimatedClassName()}`} {...props}>
              {props.children}
            </h3>
          ),
          h4: (props: any) => (
            <h4 className={`result-text text-lg font-semibold mb-2 mt-5 first:mt-0 ${getAnimatedClassName()}`} {...props}>
              {props.children}
            </h4>
          ),
          p: (props: any) => <p className={`result-text mb-0 leading-relaxed ${getAnimatedClassName()}`} {...props} />,
          ul: (props: any) => <ul className={`result-text list-disc list-outside mb-2 ml-3 space-y-0 ${getAnimatedClassName()}`} {...props} />,
          ol: (props: any) => <ol className={`result-text list-decimal list-outside mb-2 ml-3 space-y-0 ${getAnimatedClassName()}`} {...props} />,
          li: (props: any) => <li className="result-text leading-relaxed pl-1 mb-0" {...props} />,
          code: (props: any) => {
            const { inline, children, ...rest } = props;
            return inline ? (
              <code className="bg-gray-700 px-2 py-0.5 rounded text-sm font-mono" {...rest}>
                {children}
              </code>
            ) : (
              <code className={`block bg-gray-800 p-4 rounded-lg text-sm overflow-x-auto mb-5 border border-gray-700 font-mono leading-relaxed ${getAnimatedClassName()}`} {...rest}>
                {children}
              </code>
            );
          },
          pre: (props: any) => <pre className={`bg-gray-800 p-4 rounded-lg text-sm overflow-x-auto mb-5 border border-gray-700 ${getAnimatedClassName()}`} {...props} />,
          blockquote: (props: any) => (
            <blockquote className={`border-l-4 border-blue-500 pl-6 pr-4 italic mb-5 bg-gray-800/50 py-3 rounded-r ${getAnimatedClassName()}`} {...props} />
          ),
          a: (props: any) => (
            <a
              className="markdown-link"
              target="_blank"
              rel="noopener noreferrer"
              {...props}
            >
              {props.children}
            </a>
          ),
          strong: (props: any) => <strong className="font-bold" {...props} />,
          em: (props: any) => <em className="italic" {...props} />,
          hr: (props: any) => <hr className={`markdown-hr border-gray-600 my-8 ${getAnimatedClassName()}`} {...props} />,
          table: (props: any) => (
            <div className={`overflow-x-auto mb-5 ${getAnimatedClassName()}`}>
              <table className="w-full border-collapse border border-gray-700" {...props} />
            </div>
          ),
          thead: (props: any) => <thead className="bg-gray-800" {...props} />,
          tbody: (props: any) => <tbody {...props} />,
          th: (props: any) => (
            <th className="border border-gray-700 bg-gray-800 px-4 py-3 text-left font-semibold" {...props} />
          ),
          td: (props: any) => <td className="border border-gray-700 px-4 py-3" {...props} />,
          img: (props: any) => (
            <img className={`rounded-lg mb-5 max-w-full h-auto ${getAnimatedClassName()}`} alt={props.alt || ''} {...props} />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownParagraph;

